import React, { memo } from 'react';
import { motion } from 'framer-motion';
export function ModulesGrid() {
  const tomos = [{
    id: 1,
    lightColor: 'bg-[#0f1d35]/80',
    borderColor: 'border-slate-500/40',
    textColor: 'text-slate-300',
    emoji: '📘',
    title: 'Fundamentos de la Hidrología',
    subtitle: 'Todo lo que necesitás enseñar antes de hablar de ríos, cuencas o acuíferos',
    desc: 'El ciclo hidrológico completo, propiedades del agua, distribución del agua en el planeta y en Argentina, balance hídrico, regiones hídricas argentinas. Con diagramas didácticos, textos adaptados para el aula, actividades de introducción y vocabulario técnico explicado en lenguaje simple.',
    resuelve: 'El primer contacto de los alumnos con la hidrología es decisivo. Este tomo te da la base conceptual para arrancar el año con confianza y sin improvisar.',
    resultado: 'Tus alumnos entienden por qué el agua importa antes de empezar con los ríos y las cuencas. Eso cambia completamente la motivación.',
    tags: ['CICLO DEL AGUA', 'BALANCE HÍDRICO', 'REGIONES HÍDRICAS ARGENTINA']
  }, {
    id: 2,
    lightColor: 'bg-[#0f1d35]/80',
    borderColor: 'border-slate-500/40',
    textColor: 'text-slate-300',
    emoji: '📘',
    title: 'Aguas Superficiales y Cuencas Hidrográficas',
    subtitle: 'Conocé cada cuenca y cada río como si los hubieras recorrido vos mismo',
    desc: 'La Cuenca del Plata, el Río Paraná, el Río Uruguay, el sistema patagónico, las cuencas endorreicas. Características, afluentes, ciudades, caudales, conflictos hídricos. Con mapas esquemáticos, casos reales argentinos y actividades cartográficas listas para el aula.',
    resuelve: 'Cuando un alumno pregunta por qué el Paraná bajó tanto en 2021 o por qué La Pampa pelea con Mendoza por el Río Atuel, este tomo te da la respuesta exacta con datos.',
    resultado: 'Tus alumnos conectan la geografía hídrica con la realidad. Dejan de ver el mapa como algo abstracto y empiezan a verlo como algo que les importa.',
    tags: ['CUENCA DEL PLATA', 'RÍO PARANÁ', 'CONFLICTOS HÍDRICOS', 'CARTOGRAFÍA']
  }, {
    id: 3,
    lightColor: 'bg-[#0f1d35]/80',
    borderColor: 'border-slate-500/40',
    textColor: 'text-slate-300',
    emoji: '📘',
    title: 'Aguas Subterráneas y Acuíferos',
    subtitle: 'El agua invisible que sostiene la vida de millones de argentinos',
    desc: 'Acuíferos libres y confinados, el Acuífero Guaraní, el Acuífero Puelche, pozos y captación, sobreexplotación, termas de Entre Ríos. Con diagramas del subsuelo, mapa del Guaraní, casos de contaminación y gestión internacional.',
    resuelve: 'El Acuífero Guaraní es uno de los recursos estratégicos más importantes de Argentina y uno de los temas más fascinantes para trabajar ciudadanía ambiental. Este tomo te lo da completo.',
    resultado: 'Tus alumnos descubren que debajo de sus pies hay agua que viajó miles de años. Eso genera preguntas genuinas que son la base del aprendizaje real.',
    tags: ['ACUÍFERO GUARANÍ', 'AGUAS SUBTERRÁNEAS', 'PUELCHE', 'TERMAS']
  }, {
    id: 4,
    lightColor: 'bg-[#0f1d35]/80',
    borderColor: 'border-slate-500/40',
    textColor: 'text-slate-300',
    emoji: '📘',
    title: 'Hidrometeorología y Fenómenos Extremos',
    subtitle: 'El tomo que conecta la hidrología con las noticias de todos los días',
    desc: 'El ENSO (El Niño y La Niña), tormentas severas, inundaciones, sequías, cambio climático e hidrología. Casos reales: la bajante histórica del Paraná 2020-2023, la inundación de La Plata 2013, los glaciares en retroceso. Con análisis de datos, casos de estudio y debates ciudadanos.',
    resuelve: 'Cuando tus alumnos ven una noticia sobre una inundación o una sequía, este tomo te da el marco conceptual para convertirla en una clase magistral.',
    resultado: 'La hidrología deja de ser un tema del libro y se convierte en algo que tus alumnos ven en el noticiero y entienden. Eso es educación real.',
    tags: ['ENSO', 'INUNDACIONES', 'SEQUÍAS', 'CAMBIO CLIMÁTICO', 'GLACIARES']
  }, {
    id: 5,
    lightColor: 'bg-slate-800/40',
    borderColor: 'border-slate-400/50',
    textColor: 'text-slate-200',
    emoji: '⭐',
    title: 'Calidad del Agua y Contaminación Hídrica',
    subtitle: 'El tomo que transforma a tus alumnos en ciudadanos ambientales activos',
    desc: 'Parámetros de calidad del agua, contaminantes físicos, químicos y biológicos, potabilización del agua, tratamiento de aguas residuales. Casos argentinos: el Riachuelo, el arsénico en el agua del interior, la eutrofización del Embalse San Roque, el glifosato en los ríos pampeanos. Con experimentos, debates y análisis de casos reales.',
    resuelve: 'La calidad del agua es el tema más urgente y más cotidiano de la hidrología. Este tomo te da todo para trabajarlo con rigor científico y profundidad ciudadana.',
    resultado: 'Tus alumnos entienden que el agua que toman, el río que ven y el arroyo de su barrio tienen una historia política y ambiental que ellos pueden cambiar.',
    tags: ['RIACHUELO', 'ARSÉNICO', 'POTABILIZACIÓN', 'CONTAMINACIÓN', 'CIUDADANÍA'],
    badge: '⭐ EL MÁS POPULAR'
  }, {
    id: 6,
    lightColor: 'bg-[#0f1d35]/80',
    borderColor: 'border-slate-500/40',
    textColor: 'text-slate-300',
    emoji: '📘',
    title: 'Hidrología Aplicada y Proyectos Educativos',
    subtitle: 'El tomo que cierra el ciclo y convierte el conocimiento en acción',
    desc: 'Hidrología urbana, hidrología rural, gestión institucional del agua en Argentina, uso sustentable, huella hídrica. Proyectos escolares completos, guías para ferias de ciencias, planificaciones anuales listas para usar, secuencias didácticas.',
    resuelve: 'Saber hidrología no alcanza. Este tomo te da el sistema pedagógico para que tu materia sea memorable, efectiva y con impacto real en la comunidad.',
    resultado: 'Pasás de tener una asignatura que "se da" a tener un espacio de aprendizaje con proyectos reales, ciudadanía ambiental y alumnos que preguntan más de lo que el programa exige.',
    tags: ['PROYECTOS', 'FERIAS DE CIENCIAS', 'PLANIFICACIONES', 'CIUDADANÍA AMBIENTAL']
  }];
  const bonos = [{
    id: 1,
    title: '100 Actividades de Hidrología para el Aula',
    valor: '$18.000 ARS',
    desc: 'Crucigramas, sopas de letras, series de Verdadero/Falso, completar conceptos, relacionar columnas, resolución de casos reales, actividades cartográficas, experimentos, actividades grupales y debates, y actividades creativas. Todo con clave de corrección. Listo para fotocopiar o proyectar.'
  }, {
    id: 2,
    title: 'Banco de Evaluaciones y Exámenes',
    valor: '$15.000 ARS',
    desc: 'Evaluaciones diagnósticas, parciales por tomo, integradoras, evaluaciones por nivel (primaria y secundaria), rúbricas completas y claves de corrección detalladas. Nunca más armás una evaluación desde cero.'
  }, {
    id: 3,
    title: 'Atlas Hidrológico Argentino para Docentes',
    valor: '$12.000 ARS',
    desc: 'Mapas temáticos de cuencas, ríos, acuíferos, represas, cobertura de agua potable, problemas hídricos, glaciares y regímenes de precipitación. Con datos actualizados y actividades cartográficas listas para el aula. El recurso visual que faltaba.'
  }, {
    id: 4,
    title: 'Planificaciones Docentes de Hidrología',
    valor: '$10.000 ARS',
    desc: 'Planificaciones anuales para primaria y secundaria, planificaciones trimestrales, 10 secuencias didácticas completas clase por clase, objetivos por nivel, conexión explícita con los NAP y rúbricas de evaluación. Todo lo que necesitás para organizar el año en una tarde.'
  }];
  return <section className="py-20 bg-[#0f1d35]" data-id="element-158">
      <div className="container mx-auto px-4" data-id="element-159">
        <div className="text-center mb-16" data-id="element-160">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0a1428]/80 text-slate-200 border border-slate-400/50 font-bold mb-6 text-[16px]" data-id="element-161">
            📗 LOS 6 TOMOS
          </div>
          <h2 className="md:text-5xl font-extrabold text-white mb-4 text-[24px]" data-id="element-162">
            Un tomo por cada dimensión del agua en Argentina
          </h2>
          <p className="text-slate-300 font-medium text-[16px]" data-id="element-163">
            Todo el sistema didáctico resuelto para docentes de hidrología.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto mb-20" data-id="element-164">
          {tomos.map((tomo, i) => <motion.div key={tomo.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className="rounded-2xl border border-slate-500/30 bg-[#0a1428]/80 overflow-hidden shadow-lg hover:shadow-xl hover:border-slate-400/50 transition-all flex flex-col h-full relative" data-id="element-165">
              {tomo.badge && <div className="absolute top-0 right-0 bg-slate-200 text-slate-950 font-bold text-xs px-3 py-1 rounded-bl-lg z-10" data-id="element-166">
                  {tomo.badge}
                </div>}
              <div className={`${tomo.lightColor} p-4 border-b ${tomo.borderColor} flex items-center gap-3`} data-id="element-167">
                <div className="text-3xl" data-id="element-168">{tomo.emoji}</div>
                <div data-id="element-169">
                  <p className={`text-xs font-bold uppercase tracking-wider ${tomo.textColor}`} data-id="element-170">
                    Tomo {tomo.id}
                  </p>
                  <h3 className="font-bold text-white text-lg leading-tight" data-id="element-171">
                    {tomo.title}
                  </h3>
                </div>
              </div>
              <div className="p-6 flex-grow flex flex-col" data-id="element-172">
                <p className="font-semibold text-slate-300 mb-3 pb-3 border-b border-slate-500/30" data-id="element-173">
                  {tomo.subtitle}
                </p>
                <p className="text-slate-300 text-sm leading-relaxed mb-4" data-id="element-174">
                  {tomo.desc}
                </p>
                <div className="bg-[#0f1d35]/50 p-3 rounded-lg mb-4 border border-slate-500/30" data-id="element-175">
                  <p className="text-xs text-slate-400 mb-1" data-id="element-176">
                    <strong data-id="element-177">¿Qué resuelve en tu aula?</strong>
                  </p>
                  <p className="text-sm text-slate-300" data-id="element-178">{tomo.resuelve}</p>
                </div>
                <div className="bg-slate-800/30 p-3 rounded-lg mb-4 border border-slate-600/30" data-id="element-179">
                  <p className="text-xs text-slate-300 mb-1" data-id="element-180">
                    <strong data-id="element-181">Resultado:</strong>
                  </p>
                  <p className="text-sm text-slate-200" data-id="element-182">{tomo.resultado}</p>
                </div>
                <div className="mt-auto flex flex-wrap gap-1.5" data-id="element-183">
                  {tomo.tags.map((tag, j) => <span key={j} className="text-[10px] font-bold uppercase tracking-wider bg-slate-700/50 border border-slate-500/50 text-slate-200 px-2 py-1 rounded" data-id="element-184">
                      {tag}
                    </span>)}
                </div>
              </div>
            </motion.div>)}
        </div>

        <div className="text-center mb-12 border-t border-slate-500/30 pt-16" data-id="element-185">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0a1428]/80 text-slate-200 border border-slate-400/50 font-bold mb-6 text-[16px]" data-id="element-186">
            🎁 LOS 4 BONOS ESTRATÉGICOS
          </div>
          <h2 className="md:text-4xl font-extrabold text-white mb-4 text-[22px]" data-id="element-187">
            Siempre incluidos
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mb-12" data-id="element-188">
          {bonos.map((bono, i) => <motion.div key={bono.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className="rounded-2xl border border-slate-500/30 bg-[#0a1428]/80 p-6 flex flex-col h-full relative overflow-hidden" data-id="element-189">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-slate-400 to-slate-200" data-id="element-190"></div>
              <h4 className="font-bold text-slate-300 mb-2 mt-2" data-id="element-191">
                BONO 0{bono.id}
              </h4>
              <h3 className="font-bold text-white text-lg mb-3" data-id="element-192">
                {bono.title}
              </h3>
              <p className="text-xs font-bold text-slate-400 mb-4 pb-3 border-b border-slate-500/30" data-id="element-193">
                Valor real: <span className="line-through" data-id="element-194">{bono.valor}</span> →{' '}
                <span className="text-slate-200" data-id="element-195">GRATIS</span>
              </p>
              <p className="text-slate-300 text-sm leading-relaxed flex-grow" data-id="element-196">
                {bono.desc}
              </p>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="max-w-2xl mx-auto bg-gradient-to-br from-[#0f1d35] to-[#1e3a5f] rounded-2xl p-6 md:p-8 border border-slate-400/30 text-center shadow-2xl" data-id="element-197">
          <p className="text-slate-300 font-medium mb-2" data-id="element-198">
            Valor total de los 4 bonos: $55.000 ARS
          </p>
          <p className="text-2xl md:text-3xl font-black text-white mb-2" data-id="element-199">
            Tu precio hoy: <span className="text-slate-200" data-id="element-200">🎁 GRATIS</span> con
            tu Megapack
          </p>
        </motion.div>
      </div>
    </section>;
}